import MainContentTitle from '@/src/components/molecule/MainContentTitle';
import Filter from '@/src/components/organism/Filter';
import { partnershipFilters } from './filter';
import SectionCardNoneTitle from '@/src/components/molecule/SectionCardNoneTitle';
import Typo from '@/src/components/atom/Typo';
import Button from '@/src/components/atom/Button';
import LucideIcon from '@/src/components/atom/Icons/LucideIcon';
import Table from '@/src/components/atom/Table';
import { useEffect, useRef, useState } from 'react';
import { AgGridReact } from 'ag-grid-react';
import { partnershipCompaniesListColumn } from './column';
import { useQuery } from '@tanstack/react-query';
import {
  getPartnershipCompainesByIdAPI,
  getPartnershipCompaniesAPI,
} from '@/src/apis/rest/partnershipCompanies';
import usePaginationQuery from '@/src/hooks/usePaginationQuery';
import useQueryValues from '@/src/hooks/useQueryValues';
import PartnershipCompaniesCreateModal from './components/PartnershipCompaniesCreateModal';
import PartnershipCompaniesDetailModal from './components/PartnershipCompaniesDetailModal';
import PartnershipCompaniesEditModal from './components/PartnershipCompaniesEditModal';

type ModalState = 'NONE' | 'CREATE' | 'EDIT' | 'DETAIL';

const PartnershipCompaniesListPage = () => {
  const { defaultValues } = useQueryValues();
  const { page, pageSize, handlePageChange } = usePaginationQuery();

  const gridRef = useRef<AgGridReact>(null);
  const [values, setValues] = useState(defaultValues);
  const [modalState, setModalState] = useState<ModalState>('NONE');
  const [selectedId, setSelectedId] = useState<number | null>(null);

  const {
    data: partnershipListData,
    isError,
    isFetching,
    refetch: refetchPartnershipCompaniesList,
  } = useQuery({
    queryKey: ['partnership-companies', values, page, pageSize],
    queryFn: () => {
      return getPartnershipCompaniesAPI({
        ...values,
        page,
        pageSize,
      });
    },
  });

  const {
    data: partnershipCompaniesDetailData,
    refetch: refetchPartnershipCompaniesDetail,
  } = useQuery({
    queryKey: ['partnership-companies', selectedId],
    queryFn: () => {
      return getPartnershipCompainesByIdAPI({
        id: Number(selectedId),
      });
    },
    enabled: !!selectedId,
  });

  const handleDetailClick = (id: number) => {
    setSelectedId(id);
    handleOpenModal('DETAIL');
  };
  const handleOpenModal = (type: ModalState) => {
    setModalState(type);
  };
  const handleCloseModal = () => {
    setModalState('NONE');
  };
  const handleToggleModal = (type: ModalState) => {
    if (modalState === type) {
      handleCloseModal();
    } else {
      setModalState(type);
    }
  };

  useEffect(() => {
    if (isFetching && gridRef.current) {
      gridRef.current.api?.showLoadingOverlay();
    }
  }, [isFetching]);

  return (
    <>
      <MainContentTitle
        pageTitle="제휴사 관리"
        breadcrumbList={['제휴사 관리']}
      />

      <div className="mb-20 flex flex-col gap-6">
        <Filter
          title="검색 필터"
          filters={partnershipFilters()}
          onSubmit={values => {
            handlePageChange(1, pageSize, false);
            setValues(values);
          }}
        />

        <div>
          <SectionCardNoneTitle
            contentProps={{
              className: 'flex flex-col gap-y-4',
            }}
          >
            <div className="flex items-center justify-between">
              <Typo typoType="b18m">제휴사 목록</Typo>

              <Button
                buttonSize="medium"
                onClick={() => {
                  handleOpenModal('CREATE');
                }}
              >
                <div className="flex items-center gap-x-2">
                  <LucideIcon name="Plus" color="white" />
                  제휴사 정보 등록
                </div>
              </Button>
            </div>

            <Table
              ref={gridRef}
              rowData={isError ? [] : partnershipListData?.rows || []}
              isPaginationDataMapping
              columnDefs={partnershipCompaniesListColumn(e => {
                handleDetailClick(e);
              })}
              total={partnershipListData?.count || 0}
              page={page}
              pageSize={pageSize}
              handlePaginationClick={handlePageChange}
            />
          </SectionCardNoneTitle>
        </div>
      </div>

      {/* 모달 */}
      {partnershipCompaniesDetailData && modalState === 'DETAIL' && (
        <PartnershipCompaniesDetailModal
          open={modalState === 'DETAIL'}
          onOpenChange={e => {
            if (!e) setSelectedId(null);
            handleToggleModal('DETAIL');
          }}
          data={partnershipCompaniesDetailData}
          onOkClick={() => handleOpenModal('EDIT')}
        />
      )}

      {partnershipCompaniesDetailData && modalState === 'EDIT' && (
        <PartnershipCompaniesEditModal
          open={modalState === 'EDIT'}
          onOpenChange={handleCloseModal}
          onOk={() => {
            refetchPartnershipCompaniesList();
            refetchPartnershipCompaniesDetail();
            setModalState('DETAIL');
          }}
          data={partnershipCompaniesDetailData}
        />
      )}

      {modalState === 'CREATE' && (
        <PartnershipCompaniesCreateModal
          open={modalState === 'CREATE'}
          onOpenChange={handleCloseModal}
          onOk={id => {
            refetchPartnershipCompaniesList();
            refetchPartnershipCompaniesDetail();
            setSelectedId(id);
            setModalState('DETAIL');
          }}
        />
      )}
    </>
  );
};

export default PartnershipCompaniesListPage;
